/** biome-ignore-all lint/style/noRestrictedImports: date-fns imports are allowed in @cobliteam/utils only. All other packages must import from @cobliteam/utils/date. */
import { formatDuration, getTime, intervalToDuration, parse } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toZonedTime } from 'date-fns-tz';
import { DEFAULT_TZ } from '../date/constants';

/**
 * Formats a duration (in milliseconds) into a compact human-readable string.
 * Example:
 *   7260000 → "2h 1min"
 *   90000 → "1min 30s"
 *   90000000 → "1d 1h" (showDays = true)
 *   90000000 → "25h" (showDays = false)
 */
export function formatDurationShort(durationInMilliseconds: number, showSeconds = false, showDays = false): string {
  const {
    days = 0,
    hours = 0,
    minutes = 0,
    seconds = 0,
  } = intervalToDuration({
    start: 0,
    end: durationInMilliseconds,
  });

  const totalHours = showDays ? hours : days * 24 + hours;
  const parts: string[] = [];

  if (showDays && days > 0) parts.push(`${days}d`);
  if (totalHours > 0) parts.push(`${totalHours}h`);
  if (minutes > 0) parts.push(`${minutes}min`);
  if (showSeconds && seconds > 0) parts.push(`${seconds}s`);

  return parts.length ? parts.join(' ') : showSeconds ? '0s' : '0min';
}

/**
 * Formats a duration (in milliseconds) into a full localized string.
 * Example:
 *   7260000 → "2 horas 1 minuto"
 */
export function formatDurationLong(durationInMilliseconds: number): string {
  const duration = intervalToDuration({ start: 0, end: durationInMilliseconds });
  return formatDuration(duration, { locale: ptBR });
}

/**
 * Converts total minutes into { hours, minutes } object.
 * Example: 125 → { hours: 2, minutes: 5 }
 */
export function formatMinutesToHours(totalMinutes = 0) {
  const duration = intervalToDuration({ start: 0, end: totalMinutes * 60_000 });
  const { hours = 0, minutes = 0 } = duration;
  return { hours, minutes };
}

/**
 * Formats a date to a number in the specified timezone.
 * Example: 2025-01-01T00:00:00.000Z → 1735708800000
 */
export function getTimeTZ(dateString: string, formatStr: string, timezone: string = DEFAULT_TZ): number {
  const parsed = parse(dateString, formatStr, new Date());
  const zonedDate = toZonedTime(parsed, timezone);
  return getTime(zonedDate);
}
